import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Eye,
  Plus,
  Calendar,
  Clock,
  Users,
  MapPin,
  Trophy,
  Edit,
  Trash2,
  FileText,
  Upload,
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Tournament {
  _id: string;
  title: string;
  sport: string;
  description: string;
  location: string;
  venue?: string;
  date: string;
  time: string;
  registrationDeadline: string;
  maxParticipants: number;
  participantCount?: number;
  status: string;
  bannerImage?: string;
  brochure?: string;
  organizer?: string;
}

const AdminTournaments: React.FC = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<Tournament>>({});
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const brochureInputRef = useRef<HTMLInputElement>(null);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      // status='' asks the API for every status (it defaults to upcoming only)
      const res = await api.get('/tournaments', { params: { status: '', limit: 100 } });
      if (res.data.success) setTournaments(res.data.data);
    } catch (err) {
      console.error('Failed to load tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTournaments();
  }, []);

  const handleSave = async () => {
    // Validate required fields
    if (!formData.title || !formData.sport || !formData.date || !formData.time || !formData.maxParticipants || !formData.description || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (formData._id) {
        await api.put(`/tournaments/${formData._id}`, formData);
        toast.success('Tournament updated successfully');
      } else {
        await api.post('/tournaments', formData);
        toast.success('Tournament created successfully');
      }
      setShowForm(false);
      setFormData({});
      loadTournaments();
    } catch (err: any) {
      console.error('Failed to save tournament:', err);
      // Server-provided message wins over the axios generic "Request failed" one.
      const errorMessage = err?.response?.data?.message
        || err?.response?.data?.error?.message
        || err?.message
        || 'Failed to save tournament';
      toast.error(errorMessage);
    }
  };

  const handleBrochureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF or image)
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file (JPEG, PNG, WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingBrochure(true);
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'tournament-brochures');
      uploadData.append('type', 'image');

      const response = await api.post('/upload/single', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setFormData({ ...formData, brochure: response.data.data.url });
        toast.success('Brochure uploaded successfully');
      } else {
        toast.error('Failed to upload brochure');
      }
    } catch (err) {
      console.error('Brochure upload error:', err);
      toast.error('Failed to upload brochure');
    } finally {
      setUploadingBrochure(false);
      if (brochureInputRef.current) brochureInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this tournament?')) return;
    try {
      await api.delete(`/tournaments/${id}`);
      loadTournaments();
    } catch (err) {
      console.error('Failed to delete tournament:', err);
    }
  };

  const openForm = (tournament?: Tournament) => {
    setFormData(tournament || {});
    setShowForm(true);
  };

  const filteredTournaments = tournaments.filter(
    (t) =>
      t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.sport.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Manage Tournaments</h1>
        <button
          onClick={() => openForm()}
          className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Tournament
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search tournaments..."
          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
          <p className="text-gray-500 mt-2">Loading tournaments...</p>
        </div>
      ) : filteredTournaments.length > 0 ? (
        <div className="space-y-3">
          {filteredTournaments.map((t) => (
            <div
              key={t._id}
              className="flex justify-between items-center bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-100 flex items-center justify-center rounded-lg">
                  <Trophy className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t.title}</h3>
                  <p className="text-sm text-gray-500">
                    {t.sport} • {t.location} • {new Date(t.date).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => navigate(`/admin/tournaments/${t._id}`)}
                  className="p-2 rounded-md bg-gray-100 hover:bg-gray-200"
                  title="View"
                >
                  <Eye className="w-4 h-4 text-gray-700" />
                </button>
                <button
                  onClick={() => openForm(t)}
                  className="p-2 rounded-md bg-yellow-100 hover:bg-yellow-200"
                  title="Edit"
                >
                  <Edit className="w-4 h-4 text-yellow-700" />
                </button>
                <button
                  onClick={() => handleDelete(t._id)}
                  className="p-2 rounded-md bg-red-100 hover:bg-red-200"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 text-red-700" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">No tournaments found</div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {formData._id ? 'Edit Tournament' : 'Add Tournament'}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Tournament title"
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sport *</label>
                <select
                  value={formData.sport || ''}
                  onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">Select Sport</option>
                  <option value="football">Football</option>
                  <option value="cricket">Cricket</option>
                  <option value="tennis">Tennis</option>
                  <option value="chess">Chess</option>
                  <option value="basketball">Basketball</option>
                  <option value="swimming">Swimming</option>
                  <option value="badminton">Badminton</option>
                  <option value="athletics">Athletics</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tournament description"
                  rows={3}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <input
                  value={formData.location || ''}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Location"
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={formData.date ? formData.date.substring(0, 10) : ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
                  <input
                    type="time"
                    value={formData.time || ''}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Participants *</label>
                  <input
                    type="number"
                    value={formData.maxParticipants || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, maxParticipants: parseInt(e.target.value) })
                    }
                    placeholder="Max Participants"
                    min={2}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Deadline *</label>
                <input
                  type="date"
                  value={formData.registrationDeadline ? formData.registrationDeadline.substring(0, 10) : ''}
                  onChange={(e) => setFormData({ ...formData, registrationDeadline: e.target.value })}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organizer (optional)</label>
                <input
                  value={formData.organizer || ''}
                  onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
                  placeholder="Leave blank to use your name"
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>

              {/* Brochure Upload */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Brochure (PDF or Image)</label>
                <input
                  ref={brochureInputRef}
                  type="file"
                  accept=".pdf,image/*"
                  onChange={handleBrochureUpload}
                  className="hidden"
                />
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => brochureInputRef.current?.click()}
                    disabled={uploadingBrochure}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    {uploadingBrochure ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {uploadingBrochure ? 'Uploading...' : 'Upload Brochure'}
                  </button>
                  {formData.brochure && (
                    <div className="flex items-center space-x-2 text-sm text-green-600">
                      <FileText className="w-4 h-4" />
                      <a href={formData.brochure} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-700">
                        View Brochure
                      </a>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, brochure: undefined })}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 space-x-2">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTournaments;
